/**
 * 对外可被检索的页面清单——SEO 元信息的唯一来源。
 *
 * 客户端（App.tsx）与预渲染脚本（scripts/prerender.mjs）都读这张表，
 * 两处各写一份文案必然漂移，而「静态 HTML 的 title 和 JS 跑完后的 title 不一致」
 * 正是搜索引擎最典型的判降权信号。
 *
 * 口径：
 * • 只有本表列出的路径允许被收录，其余（工作台、分享令牌页、重置密码页、404）默认 noindex，
 *   新增业务路由不改这里就不会被误公开。
 * • 文案只写页面上真存在的东西：平台是预约订单与对账工具，不代收代付、不担保交易，
 *   所以描述里不出现「保证」「第一」「最」这类承诺或绝对化用语（广告法风险，也与条款口径冲突）。
 * • brandSuffix 只在预渲染产物里出现；运行时的后缀是后台下发的站点名，
 *   后台改名时静态与动态标题会短暂错开，下次构建即回正，故不额外做同步。
 */

export const BRAND_FALLBACK = '活动精英订单预约管理系统';

export type SeoPage = {
  /** 路由表里的字面 path；预渲染脚本据此校验本表与 router/config.tsx 没有走偏 */
  path: string;
  /** 页面名，运行时拼成「页面名 - 站点名」，只在工作台之外的公开页生效 */
  title: string;
  /** 检索结果里的摘要行，控制在 80 个汉字内，超出会被截断 */
  description: string;
  /** 是否产出静态 HTML（无 JS 的抓取端也能读到正文） */
  prerender: boolean;
  /** 是否进 sitemap.xml；重复内容只收 canonical 那一条 */
  inSitemap: boolean;
  /** 收录时指向的规范地址；缺省指向自身 */
  canonical?: string;
};

export const PUBLIC_PAGES: SeoPage[] = [
  {
    path: '/',
    title: '订单预约 · 客户档案 · 收款对账管理平台',
    description:
      '面向线下服务与活动行业的预约订单管理平台：客户档案、下单与审核、派工、收款记录与待收余款，按客户或按周期导出台账与对账清单，一个账号在线完成。',
    prerender: true,
    inSitemap: true,
  },
  {
    // 与站点根是同一份组件、同一套文案，规范地址指回 /，避免两条重复页各自排名。
    path: '/login',
    title: '登录',
    description:
      '登录活动精英订单预约管理系统，支持用户名、手机号或邮箱登录与扫码登录，未注册可在此提交实名申请。',
    prerender: true,
    inSitemap: false,
    canonical: '/',
  },
  {
    path: '/register',
    title: '注册账号',
    description:
      '注册活动精英订单预约管理系统账号：填写真实姓名与联系方式，完成实名核验后开通，用于管理自己的预约订单与客户档案。',
    // 不产出静态正文：RegisterModal 的返回整棵树套在 createPortal(…, document.body) 里，
    // 而 React 的服务端渲染器明确不支持 Portal（官方建议「条件渲染，只在客户端出现」）。
    // 为了一个表单元件去给业务代码加 SSR 分支不值当，也不该；收录指令与规范地址照常给，
    // 只是这个路径的 HTML 里 <div id="root"> 保持空壳。
    prerender: false,
    inSitemap: true,
  },
  {
    path: '/forgot-password',
    title: '找回密码',
    description: '通过注册邮箱接收一次性重置链接，重设活动精英订单预约管理系统的登录密码。',
    prerender: true,
    inSitemap: true,
  },
  {
    path: '/about',
    title: '关于我们',
    description:
      '活动精英订单预约管理系统的平台定位、服务方式与团队价值观说明，含数据安全保障与客服支持承诺。',
    prerender: true,
    inSitemap: true,
  },
  {
    path: '/help',
    title: '帮助中心',
    description:
      '预约订单平台使用指南：注册与实名核验、下单与审核、收款记录、导出对账台账的常见问题解答，并可提交工单或预约演示。',
    prerender: true,
    inSitemap: true,
  },
  {
    path: '/download',
    title: 'App 下载',
    description:
      '活动精英订单预约管理系统移动端下载入口，安卓、苹果与鸿蒙三平台的安装包与上架状态在此页实时展示。',
    prerender: true,
    inSitemap: true,
  },
  {
    path: '/contact',
    title: '联系我们',
    description:
      '联系活动精英订单预约管理系统客服：技术支持邮箱、客服电话、工作时间与公司地址，也可在本页直接留言。',
    prerender: true,
    inSitemap: true,
  },
  {
    path: '/terms',
    title: '服务条款',
    description:
      '平台服务性质与责任边界、账号与实名要求、订单数据归属与留存规则、违规处理与争议解决方式的正式约定。',
    prerender: true,
    inSitemap: true,
  },
  {
    path: '/privacy',
    title: '隐私政策',
    description:
      '说明平台如何收集、使用、存储与保护个人信息，实名核验涉及的证件信息处理口径，以及查询、更正、删除与注销的行使方式。',
    prerender: true,
    inSitemap: true,
  },
];

/** 按 pathname 命中公开页；只认精确相等，带令牌的深链（/share/xxx）天然落不进来 */
export function matchSeoPage(pathname: string): SeoPage | undefined {
  return PUBLIC_PAGES.find(p => p.path === pathname);
}

/** 未命中公开页的一切路径（工作台、分享页、重置密码、404）都不进索引 */
export function robotsFor(pathname: string): 'index,follow' | 'noindex,nofollow' {
  return matchSeoPage(pathname) ? 'index,follow' : 'noindex,nofollow';
}

export function canonicalFor(pathname: string): string {
  const page = matchSeoPage(pathname);
  return page?.canonical ?? page?.path ?? pathname;
}
